import { useCallback, useState, useMemo, useEffect, useRef } from "react";

import NavBar from "../Components/Navbars/main";
import Sidebar from "../Components/Sidebars/main";
import Footer from "../Components/common/footer";
import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";
// import NewChangesTabs from "../Components/new-client-dashboard/NewChangesTabs";
import ActionBarComponent from "../Components/common/ActionBarComponent";
// import DraftsLogo from "../assets/drafts/Drafts-icon.svg";
import AllTabs from "../Components/DraftsPage/tabs/allTabs";
import ClientTab from "../Components/DraftsPage/tabs/client";
import LitigationTab from "../Components/DraftsPage/tabs/litigation";
import DefendantTab from "../Components/DraftsPage/tabs/defendant";
import InsuranceTab from "../Components/DraftsPage/tabs/insurance";
import { useDocumentDrafts } from "../Components/DraftsPage/hooks/useDocumentDrafts";
import CourtformsIcon from "../assets/drafts/courtforms.svg";
import CourtFormsTab from "../Components/DraftsPage/tabs/court-forms";
import FooterWP from "../Components/common/footer-wp";
import WordProcessorTab from "../Components/DraftsPage/tabs/WordprocessorTab";
import { fetchPageObject, getCaseId } from "../Utils/helper";
import SmartResponsiveTabsContainer from "../Components/DefendantPage/DefandantMain/components/ResponsiveTabDefendants";
import { api_without_cancellation } from "../api/api";

const PageContent = ({ currentPage, data, refetchDrafts, onOpenWordProcessor }) => {
  switch (currentPage) {
    case "all":
      return (
        <AllTabs
          data={data?.all_drafts}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    case "client":
      return (
        <ClientTab
          data={data?.client_drafts}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    case "litigation":
      return (
        <LitigationTab
          data={data?.litigation_drafts}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    case "defendant":
      return (
        <DefendantTab
          data={data?.defendant_drafts}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    case "insurance":
      return (
        <InsuranceTab
          data={data?.insurance_drafts}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    case "court-forms":
      return (
        <CourtFormsTab
          data={data?.court_forms}
          refetchDrafts={refetchDrafts}
          onOpenWordProcessor={onOpenWordProcessor}
        />
      );
    default:
      return null;
  }
};

const DraftsPage = () => {
  const [currentPage, setCurrentPage] = useState("all");
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [viewMode, setViewMode] = useState("category"); // "category" or "wordprocessor"

  const { data, refetch: refetchDrafts } = useDocumentDrafts();
  // One editor ref per tab: all tabs stay mounted, so a single shared ref would
  // point at the last-mounted editor (and get nulled when any tab unmounts),
  // making autosaves write the wrong document or silently stop.
  const editorRefs = useRef({});
  const collabTabs = useRef({});
  const setTabCollab = useCallback((tabId, isCollab) => {
    collabTabs.current[tabId] = isCollab;
  }, []);

  const nullEditorRef = useRef(null);
  const getTabEditorRef = (tabId) => {
    if (!editorRefs.current[tabId]) {
      editorRefs.current[tabId] = { current: null };
    }
    return editorRefs.current[tabId];
  };
  const caseId = getCaseId();
  const [pageObject, setPageObject] = useState(null);
  useEffect(() => {
    if (caseId && !pageObject) {
      fetchPageObject(caseId, 74).then((data) => {
        setPageObject(data);
      });
    }
  }, [caseId, pageObject]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);

  const handlePageChange = useCallback((pageType) => {
    setCurrentPage(pageType);
    setViewMode("category"); // Switch to category view when clicking category tabs
  }, []);

  const handleOpenWordProcessor = useCallback(
    (draft) => {
      const tabId = `draft-${draft.id}`;

      const existingTab = openTabs.find((tab) => tab.id === tabId);
      if (existingTab) {
        setActiveTabId(tabId);
        setViewMode("wordprocessor");
        setCurrentPage(null);
        return;
      }

      const truncatedName =
        draft.document_name.length > 8
          ? `${draft.document_name.substring(0, 8)}...`
          : draft.document_name;

      const newTab = {
        id: tabId,
        draftId: draft.id,
        docId: draft.template?.id,
        dynamicTemplateId: draft.for_template?.id,
        fullName: draft.document_name,
        truncatedName,
        type: draft.for_template?.for_page?.name === "Court Forms" ? "CourtForm" : "Draft",
        courtFormId: draft.court_form_id,
        litigationId: draft.litigation_id,
      };

      setOpenTabs((prev) => [...prev, newTab]);
      setActiveTabId(tabId);
      setViewMode("wordprocessor"); // Switch to word processor view
      setCurrentPage(null);
    },
    [openTabs]
  );

  // Persist the final editor content and release the edit lock after a tab
  // closes. Runs in the background so closing stays snappy; ordering matters
  // (the final autosave must land before delete-edit-doc releases the lock).
  const persistClosedTab = async (docId, content) => {
    try {
      if (content && content.byteLength > 0) {
        const file = new File([content], "autosave.docx", {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        const formData = new FormData();
        formData.append("doc_id", docId);
        formData.append("file", file);
        await api_without_cancellation.post("/api/update-edit-doc/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }
      const deleteData = new FormData();
      deleteData.append("doc_id", docId);
      await api_without_cancellation.post("/api/delete-edit-doc/", deleteData);
    } catch (error) {
      console.error("Error saving document on tab close:", error);
    }
  };

  // const handleCloseTab = useCallback(
  //   async (tabId) => {
  //     const tab = openTabs.find((t) => t.id === tabId);
  //     const editorRef = editorRefs.current[tabId];

  //     // Serialize the editor before removing the tab (unmounting nulls the ref
  //     // and would drop any edits still inside the 2s autosave debounce window)
  //     let content = null;
  //     if (tab?.docId && editorRef?.current) {
  //       try {
  //         content = await editorRef.current.save();
  //       } catch (error) {
  //         console.error("Error serializing document on tab close:", error);
  //       }
  //     }
  //     if (tab?.docId) {
  //       persistClosedTab(tab.docId, content);
  //     }
  //     delete editorRefs.current[tabId];

  //     setOpenTabs((prev) => {
  //       const filtered = prev.filter((tab) => tab.id !== tabId);

  //       if (activeTabId === tabId) {
  //         if (filtered?.length > 0) {
  //           setActiveTabId(filtered[0]?.id);
  //           setViewMode("wordprocessor");
  //         } else {
  //           setActiveTabId(null);
  //           setViewMode("category"); // Go back to category view when last tab is closed
  //           setCurrentPage("all");
  //         }
  //       }

  //       return filtered;
  //     });
  //   },
  //   [activeTabId, openTabs]
  // );

  const handleCloseTab = useCallback(
    async (tabId) => {
      const tab = openTabs.find((t) => t.id === tabId);
      const editorRef = editorRefs.current[tabId];
      const isCollab = collabTabs.current[tabId] === true;

      // Collab docs handle their own final export + lock release inside WPBase
      // (on unmount). The legacy update-edit-doc/delete-edit-doc path would
      // release a lock the collab doc never took and race the backend export,
      // so skip it entirely for collab tabs.
      if (!isCollab) {
        // Serialize the editor before removing the tab (unmounting nulls the ref
        // and would drop any edits still inside the 2s autosave debounce window)
        let content = null;
        if (tab?.docId && editorRef?.current) {
          try {
            content = await editorRef.current.save();
          } catch (error) {
            console.error("Error serializing document on tab close:", error);
          }
        }
        if (tab?.docId) {
          persistClosedTab(tab.docId, content);
        }
      }
      delete editorRefs.current[tabId];
      delete collabTabs.current[tabId];
      setOpenTabs((prev) => {
        const filtered = prev.filter((tab) => tab.id !== tabId);

        if (activeTabId === tabId) {
          if (filtered?.length > 0) {
            setActiveTabId(filtered[0]?.id);
            setViewMode("wordprocessor");
          } else {
            setActiveTabId(null);
            setViewMode("category"); // Go back to category view when last tab is closed
            setCurrentPage("all");
          }
        }

        return filtered;
      });
    },
    [activeTabId, openTabs]
  );

  // Click handler for word processor tabs
  const handleWordProcessorTabClick = useCallback((tabId) => {
    setActiveTabId(tabId);
    setCurrentPage(tabId);
    setViewMode("wordprocessor");
  }, []);

  const draftCounts = useMemo(
    () => ({
      all: data?.all_drafts?.length || 0,
      client: data?.client_drafts?.length || 0,
      defendant: data?.defendant_drafts?.length || 0,
      insurance: data?.insurance_drafts?.length || 0,
      litigation: data?.litigation_drafts?.length || 0,
      courtForms: data?.court_forms?.length || 0,
    }),
    [data]
  );

  const mainTabs = useMemo(() => {
    const allTabs = [
      {
        id: "all",
        label: "All",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("all"),
        leftHand: false,
      },
      {
        id: "client",
        label: "Client",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("client"),
        leftHand: false,
      },
      {
        id: "defendant",
        label: "Defendant",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        left: "0px",
        onClick: () => handlePageChange("defendant"),
        leftHand: false,
      },
      {
        id: "insurance",
        label: "Insurance",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("insurance"),
        leftHand: false,
      },
      {
        id: "litigation",
        label: "Litigation",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        left: "0px",
        onClick: () => handlePageChange("litigation"),
        leftHand: false,
      },
      {
        id: "court-forms",
        label: "Court Forms",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        left: "0px",
        onClick: () => handlePageChange("court-forms"),
        leftHand: true,
        span: (
          <span
            className="ic-avatar ic-15 d-flex align-items-center m-r-5"
            style={{
              width: "15px",
              height: "15px",
            }}
          >
            <img src={CourtformsIcon} width="25" alt="Court Forms" />
          </span>
        ),
      },
    ];

    return allTabs.filter((tab) => {
      if (tab.id === "all") return true;
      if (tab.id === "client") return draftCounts.client > 0;
      if (tab.id === "defendant") return draftCounts.defendant > 0;
      if (tab.id === "insurance") return draftCounts.insurance > 0;
      if (tab.id === "litigation") return draftCounts.litigation > 0;
      if (tab.id === "court-forms") return draftCounts.courtForms > 0;
      return true;
    });
  }, [handlePageChange, draftCounts]);

  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);

  const activeTab = openTabs.find((tab) => tab.id === activeTabId);
  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName="74" />
          <ActionBarComponent
            src={pageObject?.page_icon}
            // src="https://simplefirm-bucket.s3.amazonaws.com/static/BP_resources/images/icon/car-accident-icon-color.svg"
            page_name={pageObject?.name}
            pageObj={pageObject}
            isChecklist={pageObject?.PageChklst || false}
          />

          <div
            className="TreatmentPage-main-container invisible-scrollbar"
            style={{
              cursor: "pointer",
            }}
          >
            {/* Combined tab row: Category tabs on left, Word Processor tabs on right */}
            <div className="d-flex justify-content-between">
              {/* <div style={{ width: openTabs.length > 0 ? "60%" : "100%" }}>
                <NewChangesTabs
                  currentTab={currentPage} // Only highlight if in category view
                  tabsData={mainTabs}
                  height={25}
                  labelClass=" justify-content-start"
                  page="draft"
                />
              </div> */}

              <SmartResponsiveTabsContainer
                isEmpty={data?.length === 0}
                HandlePage={currentPage}
                tabsData={mainTabs}
                viewTab={[]}
                addTab={[]}
                photoTab={[]}
                handlePageChange={handlePageChange}
                viewType={""}
              />

              {openTabs.length > 0 && (
                <div
                  className="d-flex align-items-center justify-content-end"
                  style={{ gap: "5px", padding: "0 10px" }}
                >
                  {openTabs.map((tab) => (
                    <div
                      key={tab.id}
                      className="d-flex align-items-center"
                      style={{
                        height: "25px",
                        padding: "0 10px",
                        backgroundColor:
                          viewMode === "wordprocessor" && activeTabId === tab.id
                            ? "var(--primary)"
                            : "var(--primary-15)",
                        color:
                          viewMode === "wordprocessor" && activeTabId === tab.id
                            ? "white"
                            : "black",
                        cursor: "pointer",
                        borderRadius: "3px 3px 0 0",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                      onClick={() => handleWordProcessorTabClick(tab.id)}
                    >
                      <span
                        style={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tab.truncatedName}
                      </span>
                      <span
                        className="ms-2"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.id);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Show menu bar with full filename when in word processor view */}
            {viewMode === "wordprocessor" && activeTab && (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  height: "25px",
                  backgroundColor: "var(--primary)",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {activeTab.fullName}
              </div>
            )}

            {/* Content area - conditional rendering based on view mode */}
            <div style={{ display: viewMode === "category" ? "block" : "none" }}>
              <PageContent
                currentPage={currentPage}
                data={data}
                refetchDrafts={refetchDrafts}
                onOpenWordProcessor={handleOpenWordProcessor}
              />
              <NotesSectionDashboard pageName={"Drafts"} />
            </div>

            {/* Word processor tabs - keep all mounted but only show active */}
            {openTabs.map((tab) => (
              <div
                key={tab.id}
                style={{
                  height: "calc(100% - 55px)",
                  display:
                    viewMode === "wordprocessor" && activeTabId === tab.id ? "block" : "none",
                }}
              >
                <WordProcessorTab
                  tab={tab}
                  setPageNumber={setPageNumber}
                  setTotalPages={setTotalPages}
                  container={getTabEditorRef(tab.id)}
                  setTabCollab={setTabCollab}
                  handleCloseTab={(e) => {
                    e.stopPropagation();
                    handleCloseTab(tab.id);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {viewMode === "wordprocessor" ? (
        <FooterWP
          container={activeTabId ? getTabEditorRef(activeTabId) : nullEditorRef}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          totalPages={totalPages}
          setTotalPages={setTotalPages}
          zoomScale={zoomScale}
          setZoomScale={setZoomScale}
        />
      ) : (
        <Footer />
      )}
    </>
  );
};

export default DraftsPage;
